import React, { useState, useEffect, useCallback } from "react"
import { useLocation } from "react-router-dom"
import { Container, Table, Alert, Pagination, PaginationItem, PaginationLink } from "reactstrap"
import { useMutation, useQuery } from "@tanstack/react-query"
import Swal from "sweetalert2"
import withReactContent from "sweetalert2-react-content"
import { debounce } from "lodash"

import storage from "services/storage"
import { authUserKey } from "config/API"
import { getCustomers, deleteCustomer } from "services/customers"
import { Loading } from "components"
import { CreateModalForm, EditModalForm, ImportModalForm } from "components/customers"
import { useDownloadTemplate } from "hooks/use-download-template"
import ListHeader from "ui-componets/list-header"
import CustomersItemList from "components/customers/customers-item-list"
import Shortcuts from "components/Shortcuts"

const CustomersList = () => {
  document.title = "Lista de clientes | Punto de venta"

  const Location = useLocation()

  const [filters, setFilters] = useState("")
  const [searchTxt, setSearchTxt] = useState(new URLSearchParams(Location.search).get("search") ?? "")
  const [modalCreate, setmodalCreate] = useState(false)
  const [modalImport, setmodalImport] = useState(false)
  const [modalEdit, setmodalEdit] = useState(false)
  const [customerId, setCustomerId] = useState(null)
  const [showAlertSuccess, setShowAlertSuccess] = useState(false)
  const [msgSuccess, setMsgSuccess] = useState("")

  const ModalCustomer = withReactContent(Swal)

  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.addEventListener("mouseenter", Swal.stopTimer)
      toast.addEventListener("mouseleave", Swal.resumeTimer)
    },
  })

  const customers = useQuery(["customers", filters, filters.toString()], () => getCustomers(filters))
  const customerDeleted = useMutation((id) => deleteCustomer(id))

  const permissions = storage.get(authUserKey).user_permissions || []
  const canAdd = permissions.some((e) => e.codename === "add_customer")
  const canEdit = permissions.some((e) => e.codename === "change_customer")
  const canDelete = permissions.some((e) => e.codename === "delete_customer")
  const styleCenterTable = { textAlign: "center", verticalAlign: "middle" }

  const toggleModalCreate = () => {
    setmodalCreate(!modalCreate)
  }

  const toggleModalImport = () => {
    setmodalImport(!modalImport)
  }

  const toggleModalEdit = () => {
    if (modalEdit) {
      setCustomerId("")
    }
    setmodalEdit(!modalEdit)
  }

  const openEditModal = (customerId) => {
    setCustomerId(customerId)
    toggleModalEdit()
  }

  const openDeleteModal = (row) => {
    Swal.fire({
      title: `¿Está seguro que desea eliminar al cliente: <b>${row.name}</b>?`,
      text: "Este cambio no se podrá revertir",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Aceptar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed) {
        customerDeleted.mutateAsync(row?.id).then(() => {
          customers.refetch()
          Toast.fire("!Eliminado!", `El cliente <b>${row.name}</b> ha sido eliminado correctamente`, "success")
        })
      }
    })
  }

  const handleSearch = (pageParams) => {
    const params = new URLSearchParams(filters)
    if (pageParams) {
      const newParams = new URLSearchParams(pageParams)
      window.history.replaceState({}, null, `${Location.pathname}?${newParams}`)
      setFilters(newParams)
    } else if (searchTxt) {
      params.set("search", searchTxt)
      params.delete("offset")
      params.delete("per_page")
      window.history.replaceState({}, null, `${Location.pathname}?${params}`)
      setFilters(params)
    } else {
      if (params.get("search")) {
        params.delete("offset")
        params.delete("per_page")
        params.delete("search")
        window.history.replaceState({}, null, `${Location.pathname}?${params}`)
        setFilters(params)
      }
    }
  }

  const downloadTemplate = useCallback(() => {
    useDownloadTemplate("/customers/download_template")
  }, [])

  const debouncedSearch = useCallback(
    debounce((text) => {
      const params = new URLSearchParams()
      params.set("search", text)
      params.delete("offset")
      params.delete("per_page")
      window.history.replaceState({}, null, `${Location.pathname}?${params}`)
      setFilters(params)
    }, 300),
    []
  )

  function handleChangeSearch(event) {
    setSearchTxt(event.target.value)
    debouncedSearch(event.target.value)
  }

  const isLoading = customers.isLoading || customerDeleted.isLoading

  useEffect(() => {
    setFilters(Location.search)
  }, [])

  useEffect(() => {
    if (showAlertSuccess) {
      ModalCustomer.fire({
        icon: "success",
        title: msgSuccess,
        didOpen: () => {
          customers.refetch()
          setShowAlertSuccess(false)
        },
        confirmButtonText: "Aceptar",
        confirmButtonColor: "green",
      })
    }
  }, [showAlertSuccess])

  return !permissions.some((e) => e.codename === "view_customer") ? (
    <div className="page-content">
      <div className="text-center">
        <h1>No autorizado para ver esta pagina</h1>
      </div>
    </div>
  ) : (
    <React.Fragment>
      <div className="page-content">
        {canAdd && (
          <CreateModalForm
            isOpen={modalCreate}
            toggle={toggleModalCreate}
            setShowAlertSuccess={setShowAlertSuccess}
            setMsgSuccess={setMsgSuccess}
          />
        )}
        {canAdd && (
          <ImportModalForm
            isOpen={modalImport}
            toggle={toggleModalImport}
            setShowAlertSuccess={setShowAlertSuccess}
            setMsgSuccess={setMsgSuccess}
          />
        )}
        {canEdit && customerId && (
          <EditModalForm
            isOpen={modalEdit}
            toggle={toggleModalEdit}
            customerId={customerId}
            setShowAlertSuccess={setShowAlertSuccess}
            setMsgSuccess={setMsgSuccess}
          />
        )}
        <Container fluid>
          <Alert color="danger" isOpen={customers.isError}>
            {customers?.error?.errors?.map((error) => error?.message)}
          </Alert>

          <h2>Lista de Clientes</h2>
          <div className="bg-white p-3">
            <ListHeader
              canAdd={canAdd}
              handleOnChangeSearch={handleChangeSearch}
              handleSearch={handleSearch}
              searchTxt={searchTxt}
              toggleModalCreate={toggleModalCreate}
              toggleModalImport={toggleModalImport}
            />
            {!customers.error && isLoading ? (
              <div className="my-5 text-center">
                <Loading isLoading={isLoading} size={2} />
              </div>
            ) : customers.error && !isLoading ? (
              <div className="my-5 text-center">
                <h1>Error intenta mas tarde</h1>
              </div>
            ) : (
              <div style={{ marginTop: "30px" }} className="table-responsive">
                <Table>
                  <thead>
                    <tr>
                      <th style={styleCenterTable}>Nombre</th>
                      <th style={styleCenterTable}>Email</th>
                      <th style={styleCenterTable}>Teléfono</th>
                      <th style={styleCenterTable}>Código Postal</th>
                      <th style={styleCenterTable}>RFC</th>
                      <th style={styleCenterTable}>Límite de crédito</th>
                      <th style={styleCenterTable}>Días de crédito</th>
                      <th style={styleCenterTable}>Tipo de precio</th>
                      <th style={styleCenterTable}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers?.data?.results.length > 0 ? (
                      customers?.data?.results?.map((item) => {
                        return (
                          <CustomersItemList
                            key={item.id}
                            item={item}
                            canDelete={canDelete}
                            canEdit={canEdit}
                            openDeleteModal={openDeleteModal}
                            openEditModal={openEditModal}
                          />
                        )
                      })
                    ) : (
                      <tr>
                        <td colSpan={6} className="text-center">
                          No hay datos
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>

                <div className="text-center">
                  {(customers.data?.next || customers.data?.previous) && (
                    <Pagination aria-label="Page navigation" className="d-flex justify-content-center">
                      {customers.data?.previous && (
                        <PaginationItem>
                          <PaginationLink
                            previous
                            onClick={() => handleSearch(customers.data?.previous.split("?")[1])}
                          />
                        </PaginationItem>
                      )}
                      {customers.data?.next && (
                        <PaginationItem>
                          <PaginationLink next onClick={() => handleSearch(customers.data?.next.split("?")[1])} />
                        </PaginationItem>
                      )}
                    </Pagination>
                  )}
                </div>
              </div>
            )}
          </div>
        </Container>
      </div>
      <Shortcuts
        toggleModalCreate={toggleModalCreate}
        toggleModalImport={toggleModalImport}
        downloadTemplate={downloadTemplate}
      />
    </React.Fragment>
  )
}

export default CustomersList
